import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';


import { showLoading, hideLoading } from '../../../../store/app';
import WebAPIClient, {
  errorResponseToastr,
} from '../../../../api/_energytracer'

const intensity_utilities = ["total", "electric", "gas"]
const intensity_types = ["usage", "cost"]
const climate_zones = [
  "1A",
  "1B",
  "2A",
  "2B",
  "3A",
  "3B",
  "3C",
  "4A",
  "4B",
  "4C",
  "5A",
  "5B",
  "5C",
  "6A",
  "6B",
  "7",
  "8",
]


export default function QueryBenchmark() {
  const dispatch = useDispatch();

  const [requestData, setRequestData] = useState([]);

  const [zone, setZone] = useState('5A');
  const [utility, setUtility] = useState('total');
  const [intensity, setIntensity] = useState('usage');
  const [climateZoneCounts, setClimateZoneCounts] = useState({});


  const fetchData = useCallback(async () => {
    dispatch(showLoading());
    try {
      const data = await new WebAPIClient().GET('/veregy_benchmarking/query_benchmark_statistics');
      setRequestData(data)
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
    dispatch(hideLoading());
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let climate_zone_counts = {}
    climate_zones.forEach((zone) => {
      climate_zone_counts[zone] = 0;
      if (Object.keys(requestData).includes(zone) && requestData[zone]['total']['usage']) {
        climate_zone_counts[zone] = requestData[zone]['total']['usage']['quality_statistics']['count']
      }
    })
    setClimateZoneCounts(climate_zone_counts)
  }, [requestData])


  return <div>
    <h3>Benchmark Lookup</h3>
    <form>
      <List disablePadding>
        <ListItem disableGutters>
          <FormControl id='zone' variant='standard' fullWidth>
            <InputLabel id='zone-label'>Climate Zone</InputLabel>
            <Select
              labelId='zone-label'
              name='zone'
              onChange={(e) => {
                setZone(e.target.value);
              }}
              value={zone}
              MenuProps={{
                MenuListProps: { disablePadding: true },
                slotProps: {
                  paper: { sx: { minWidth: 'unset !important' } },
                },
              }}
              sx={{
                backgroundColor: 'unset',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}>
              <MenuItem value=''></MenuItem>
              {climate_zones.map((zone) => {
                return (
                  <MenuItem key={zone} value={zone}>
                    {zone} {`(${Object.keys(climateZoneCounts).includes(zone) ? climateZoneCounts[zone] : 0})`}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </ListItem>
        <ListItem disableGutters>
          <FormControl id='utility' variant='standard' fullWidth>
            <InputLabel id='utility-label'>Utility Type</InputLabel>
            <Select
              labelId='utility-label'
              name='utility'
              onChange={(e) => {
                setUtility(e.target.value);
              }}
              value={utility}
              MenuProps={{
                MenuListProps: { disablePadding: true },
                slotProps: {
                  paper: { sx: { minWidth: 'unset !important' } },
                },
              }}
              sx={{
                backgroundColor: 'unset',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}>
              <MenuItem value=''></MenuItem>
              {intensity_utilities.map((util) => {
                return (
                  <MenuItem key={util} value={util}>
                    {util}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </ListItem>
        <ListItem disableGutters>
          <FormControl id='intensity' variant='standard' fullWidth>
            <InputLabel id='intensity-label'>Intensity Type</InputLabel>
            <Select
              labelId='intensity-label'
              name='intensity'
              onChange={(e) => {
                setIntensity(e.target.value);
              }}
              value={intensity}
              MenuProps={{
                MenuListProps: { disablePadding: true },
                slotProps: {
                  paper: { sx: { minWidth: 'unset !important' } },
                },
              }}
              sx={{
                backgroundColor: 'unset',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}>
              <MenuItem value=''></MenuItem>
              {intensity_types.map((i_type) => {
                return (
                  <MenuItem key={i_type} value={i_type}>
                    {i_type}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </ListItem>
      </List>
    </form>
    <h4>Benchmark Information</h4>
    {(Object.keys(requestData).includes(zone) && Object.keys(requestData[zone]).includes(utility) && Object.keys(requestData[zone][utility]).includes(intensity) && requestData[zone][utility][intensity] !== null) ? (<BenchmarkInfo benchmarkData={requestData[zone][utility][intensity]} />) : (<div>No data available</div>)}
  </div>
}

function BenchmarkInfo(props) {
  const { benchmarkData } = props;

  const [percentile, setPercentile] = useState(50);

  return <div>

    <div>Mean: {benchmarkData.mean.toFixed(2)}</div>
    <div>Median: {benchmarkData.median.toFixed(2)}</div>
    <br></br>
    <div>Count: {benchmarkData.quality_statistics.count}</div>
    <div>Min: {benchmarkData.quality_statistics.min.toFixed(2)}</div>
    <div>Max: {benchmarkData.quality_statistics.max.toFixed(2)}</div>
    <div>Range: {benchmarkData.quality_statistics.range.toFixed(2)}</div>
    <div>Standard Deviation: {benchmarkData.quality_statistics.standard_deviation.toFixed(2)}</div>
    <br></br>
    <List disablePadding>
      <ListItem disableGutters>
        <FormControl id='percentile' variant='standard' fullWidth>
          <InputLabel id='percentile-label'>Percentile</InputLabel>
          <Select
            labelId='percentile-label'
            name='percentile'
            onChange={(e) => {
              setPercentile(e.target.value);
            }}
            value={percentile}
            MenuProps={{
              MenuListProps: { disablePadding: true },
              slotProps: {
                paper: { sx: { minWidth: 'unset !important' } },
              },
            }}
            sx={{
              backgroundColor: 'unset',
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
              },
            }}>
            <MenuItem value=''></MenuItem>
            {Array.from({ length: 101 }, (x, i) => i).map((perc) => {
              return (
                <MenuItem key={perc} value={perc}>
                  {perc}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </ListItem>
    </List>
    <div>Percentile: {benchmarkData.percentiles[percentile].toFixed(2)}</div>

  </div>
}